import { useEffect, useState, FC, useRef, useCallback } from "react";
import { Button, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, Tooltip, useBreakpointValue } from "@chakra-ui/react";
import { ImBold } from '@react-icons/all-files/im/ImBold';
import { ImItalic } from '@react-icons/all-files/im/ImItalic';
import { ImStrikethrough } from '@react-icons/all-files/im/ImStrikethrough';
import { ImList2 } from '@react-icons/all-files/im/ImList2';
import { ImQuotesLeft } from '@react-icons/all-files/im/ImQuotesLeft';
import { ChevronDownIcon } from "@chakra-ui/icons";

type Props = {
  form: any;
  forName: string;
}

type Selection = {
  start: number;
  end: number;
}

enum Formatted {
  FULLY = 'fully',
  PARTIALY = 'partially',
  NOT = 'not'
}

const defaultSelection: Selection = { start: -1, end: -1 };

export const inlineFormat = (node: any, str: string) => {
  const val = node.value;

  const start = node.selectionStart;
  const end = node.selectionEnd;

  let pre = val.substring(0, start);
  let section = val.substring(start, end);
  let post = val.substring(end);

  if (pre.endsWith(str)) {
    // Move format str inside of section
    pre = pre.substring(0, pre.length - str.length);
    section = str + section;
  }

  if (post.startsWith(str)) {
    // Move format str inside of section
    post = post.substring(str.length);
    section = section + str;
  }

  const cleanSection = section.split(str).join('');

  let output = '';

  if (section.startsWith(str) && section.endsWith(str)) {
    output = `${pre}${cleanSection}${post}`;
    return { value: output, selection: { start: pre.length, end: pre.length + cleanSection.length } };
  }

  output = `${pre}${str}${cleanSection}${str}${post}`;
  return { value: output, selection: { start: pre.length + str.length, end: pre.length + cleanSection.length + str.length } };
}

const blockLevelMarkdown = ['###', '##', '#', '-', '>'];

const linebreaksBeforePos = (str: string, pos: number) => {
  const partial = str.substring(0, pos);
  const lines = partial.split('\n');

  return lines.length - 1;
}

const amountFormatted = (lines: string[], start: number, end: number, markdown: string) : Formatted => {
  let count = 0;
  const num = end - start + 1;
  const markdownWithSpace = `${markdown} `;

  for (let x = start; x <= end; x++) {
    const line = lines[x];

    if (line.trim().startsWith(markdownWithSpace)) {
      count++;
    }
  }

  if (count === num) {
    return Formatted.FULLY;
  }
  else if (count === 0) {
    return Formatted.NOT;
  }

  return Formatted.PARTIALY;
}

const leftTrim = (str: string) => str.replace(/^\s+/, "");

const calcLeast = (lines: string[], pos: number) => {
  let total = 0;

  for (let x = 0; x < pos; x++) {
    total += lines[x].length + 1;
  }

  return total;
}

export const blockFormat = (node: any, markdown: string) => {
  let value = node.value;

  let start = node.selectionStart;
  let end = node.selectionEnd;

  const startLine = linebreaksBeforePos(value, start);
  const endLine = linebreaksBeforePos(value, end);

  const lines = value.split('\n');

  const startLeast = calcLeast(lines, startLine);

  const formatted = amountFormatted(lines, startLine, endLine, markdown);

  // A block is considered anything from the beginning of a line to a new line character (\n)
  // We need to iterate over every line where the selection touches, clearing out old block formatting and adding new.

  if (formatted === Formatted.PARTIALY || formatted === Formatted.NOT) {
    // For partially formatted and not formatted, we remove any current formatting then add the new one in.

    // Iterate through the lines that are touched by selection
    for (let x = startLine; x <= endLine; x++) {
      let line = lines[x];

      // Remove any existing block formatting from current line
      for (const m of blockLevelMarkdown) {
        const cleanLine = leftTrim(line);

        if (cleanLine.startsWith(m)) {
          const before = line.length;
          line = leftTrim(line.substring(line.indexOf(m) + 1));
          const after = line.length;

          // Adjust selection by the amount of characters removed
          end -= (before - after);

          if (x === startLine) {
            start -= (before - after);
          }
        }
      }

      // In case there is no previous formatting, but there is excess whitespace, we need to clear out that whitespace.
      const before = line.length;
      line = leftTrim(line);
      const after = line.length;

      // And again adjust the selection by the amount of characters removed
      end -= (before - after);

      if (x === startLine) {
        start -= (before - after);
      }

      // Now we are adding in the markdown at the beginning of the block 
      lines[x] = `${markdown} ${line}`;

      // and adjusting selection end by the amount added (length of markdown plus the space character)
      end += markdown.length + 1;
    }

    // and finally adjust selection start by the amount added. This only happens once because the formating will only impact selection start once.
    start += markdown.length + 1;
  }
  else {
    // For fully formatted, remove all formatting. This enables the toggle on/off feature. Since it is fully formatted with the current markdown, only the current markdown will be removed.
    // First, iterate through all the lines that selection touches.
    for (let x = startLine; x <= endLine; x++) {
      let line = lines[x];

      // remove markdown block formatting from each line
      for (const m of blockLevelMarkdown) {
        const cleanLine = leftTrim(line);

        if (cleanLine.startsWith(m)) {
          const before = line.length;
          line = leftTrim(line.substring(line.indexOf(m) + 1));
          const after = line.length;

          // Adjust selection by the number of characters removed
          end -= (before - after);

          if (x === startLine) {
            start -= (before - after);
          }
        }
      }

      lines[x] = line;
    }
  }

  // Make sure start is at least zero or at the beginning of the current line
  if (start < startLeast) {
    start = startLeast;
  }

  // Make sure end is no less than start
  if (end < start) {
    end = start;
  }

  return { value: lines.join('\n'), selection: { start, end } };
}

export const MarkdownCommands: FC<Props> = ({ form, forName, children }) => {
  const [selection, setSelection] = useState<Selection>(defaultSelection);
  const ref = useRef(null);
  const size = useBreakpointValue(['xs', 'sm']);
  const iconSize = useBreakpointValue([14, 16]);

  const applyChange = useCallback((value: string) => {
    form.setFieldValue(forName, value);
  }, [form, forName]);

  const handleInlineFormat = useCallback((event: any, str: string) => {
    event.preventDefault();
    event.stopPropagation();

    const { selection, value } = inlineFormat(ref.current, str);

    applyChange(value)
    setSelection(selection);
  }, [ref, applyChange]);

  const handleBlockFormat = useCallback((event: any, str: string) => {
    event.preventDefault();
    event.stopPropagation();

    const { selection, value } = blockFormat(ref.current, str);

    applyChange(value);
    setSelection(selection);
  }, [ref, applyChange]);

  useEffect(() => {
    if (!ref.current) return;

    const node = ref.current;

    const handleKeyDown = (event: any) => {
      if ((event.metaKey || event.ctrlKey)) {
        if (event.key === 'b') {
          handleInlineFormat(event, '*');
        }
        else if (event.key === 'i') {
          handleInlineFormat(event, '_');
        }
      }
    }

    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        const value = node.value;

        let start = node.selectionStart;
        let end = node.selectionEnd;

        const currentLine = linebreaksBeforePos(value, start);

        if (currentLine === 0) {
          return;
        }

        const lines = value.split('\n');

        const prevLine = lines[currentLine - 1];

        let prevMarkdown = null;

        if (leftTrim(prevLine).startsWith('-')) {
          prevMarkdown = '-';
        }
        else if (leftTrim(prevLine).startsWith('>')) {
          prevMarkdown = '>';
        }

        if (prevMarkdown) {
          if (prevLine.trim() === prevMarkdown) {
            lines[currentLine - 1] = '';

            start -= prevLine.length;
            end -= prevLine.length;
          }
          else {
            lines[currentLine] = `${prevMarkdown} ${lines[currentLine]}`;

            start += 2;
            end += 2;
          }

          applyChange(lines.join('\n'));
          setSelection({ start, end });
        }
      }
    }

    node.addEventListener('keydown', handleKeyDown, false);
    node.addEventListener('keyup', handleKeyPress, false);

    return () => {
      node.removeEventListener('keydown', handleKeyDown, false);
      node.removeEventListener('keyup', handleKeyPress, false);
    }
  }, [ref, applyChange, handleInlineFormat, handleBlockFormat]);

  useEffect(() => {
    if (selection.start !== selection.end || selection.start !== -1) {
      ref.current.focus();
      ref.current.setSelectionRange(selection.start, selection.end);
      setSelection(defaultSelection);
    }
  }, [ref, selection]);

  return (
    <>
      <Flex gridGap="5px" flexWrap="wrap" paddingBottom="10px">
        <Menu autoSelect={false} onClose={() => ref.current.focus()}>
          <MenuButton variant="secondary" as={Button} rightIcon={<ChevronDownIcon />} width={100} size={size}>
            Heading
          </MenuButton>
          <MenuList>
            <MenuItem onClick={(event) => handleBlockFormat(event, '#')}># H1</MenuItem>
            <MenuItem onClick={(event) => handleBlockFormat(event, '##')}>## H2</MenuItem>
            <MenuItem onClick={(event) => handleBlockFormat(event, '###')}>### H3</MenuItem>
          </MenuList>
        </Menu>

        <Tooltip label="*bold*">
          <IconButton
            aria-label="bold"
            onMouseDown={event => handleInlineFormat(event, '*')}
            variant="secondary"
            size={size}
            icon={<ImBold size={iconSize} />}
          />
        </Tooltip>

        <Tooltip label="_italic_">
          <IconButton
            aria-label="italic"
            onMouseDown={event => handleInlineFormat(event, '_')}
            variant="secondary"
            size={size}
            icon={<ImItalic size={iconSize} />}
          />
        </Tooltip>

        <Tooltip label="~strike~">
          <IconButton
            aria-label="strikethrough"
            onMouseDown={event => handleInlineFormat(event, '~')}
            variant="secondary"
            size={size}
            icon={<ImStrikethrough size={iconSize} />}
          />
        </Tooltip>

        <Tooltip label="- bullet">
          <IconButton
            aria-label="bullet"
            onMouseDown={event => handleBlockFormat(event, '-')}
            variant="secondary"
            size={size}
            icon={<ImList2 size={iconSize} />}
          />
        </Tooltip>

        <Tooltip label="> blockquote">
          <IconButton
            aria-label="block quote"
            onMouseDown={event => handleBlockFormat(event, '>')}
            variant="secondary"
            size={size}
            icon={<ImQuotesLeft size={iconSize} />}
          />
        </Tooltip>
      </Flex>
      {(children as any)(ref)}
    </>
  )
}